var m = require('mithril');
var prop = require('mithril/stream');
var go = require('gojs');
var equipmentModel = require('../../models/equipment');
var diagramConfiguration = require('./diagramConfiguration');
var edit = require('../../../layout/components/edit');
var editCustomEquipmentModal = require('../../editCustomEquipmentModal');
var createSvgTemplates = require('./equipmentTemplates/svg');
var createDatasetTemplate = require('./equipmentTemplates/dataset');
var createManifoldTemplate = require('./equipmentTemplates/manifold');
var createPortTemplate = require('./equipmentTemplates/port');
var createRoomNodeTemplate = require('./equipmentTemplates/room');
var createGroupNodeTemplate = require('./equipmentTemplates/group');
var createDamperNodeTemplate = require('./equipmentTemplates/damper');
var createFanNodeTemplate = require('./equipmentTemplates/fan');
var { createStaticSvgTemplate } = require('./equipmentTemplates/staticSvgTemplate');
var createExternalConnectionAnnotations = require('./equipmentTemplates/helpers/externalConnectionAnnotations');
var gridEquipmentNode = require('./equipmentTemplates/helpers/gridEquipmentNode');

var $ = go.GraphObject.make;
var portCoverBrush = $(go.Brush, "Linear", { 0: "#2a2a2a", 1: "#f0f0f0", start: go.Spot.Bottom, end: go.Spot.Top });
var equipmentGridDefaults = { cols: 7, rows: 5 };
var equipmentBodyBorderStrokeWidth = 2;
var bodyGridInset = 2;

function getDefaultEquipmentBodySize() {
  return new go.Size(
    (diagramConfiguration.calculateCellWidth() * 7) - bodyGridInset,
    (diagramConfiguration.calculateCellHeight() * 5) - bodyGridInset
  );
}

function getEquipmentBodySize(data) {
  const defaultSize = getDefaultEquipmentBodySize();
  const width = Number(data && data.width);
  const height = Number(data && data.height);

  if (Number.isFinite(width) && width > 0 && Number.isFinite(height) && height > 0) {
    return new go.Size(width, height);
  }
  return defaultSize;
}

function createEquipmentSelectionAdornmentTemplate() {
  return $(go.Adornment, "Auto",
    $(go.Shape, "Rectangle", {
      fill: null,
      stroke: "#4BA3E3",
      strokeWidth: 0.75
    }),
    $(go.Placeholder)
  );
}

function countPortSlots(data, arrayName) {
  const ports = data && Array.isArray(data[arrayName]) ? data[arrayName] : [];
  return ports.length * 2;
}

function calculatePortPanelOffset(data, arrayName, isHorizontal) {
  const bodySize = getEquipmentBodySize(data);
  const cellSize = isHorizontal
    ? diagramConfiguration.calculateCellWidth()
    : diagramConfiguration.calculateCellHeight();
  const bodyCellCount = Math.max(Math.round((isHorizontal ? bodySize.width : bodySize.height) / cellSize), 0);
  const portCellSlots = countPortSlots(data, arrayName);

  if (bodyCellCount <= portCellSlots || portCellSlots === 0) return 0;
  return Math.round((bodyCellCount - portCellSlots) / 2) * cellSize;
}

function createSidePortPanelMarginBinding(arrayName) {
  return new go.Binding("margin", "", data => {
    return new go.Margin(calculatePortPanelOffset(data, arrayName, false), 0, 0, 0);
  });
}

function createTopBottomPortPanelMarginBinding(arrayName) {
  return new go.Binding("margin", "", data => {
    return new go.Margin(0, 0, 0, calculatePortPanelOffset(data, arrayName, true));
  });
}

function calculateCenterPortPanelOffset(data, isHorizontal) {
  const bodySize = getEquipmentBodySize(data);
  const cellSize = isHorizontal
    ? diagramConfiguration.calculateCellWidth()
    : diagramConfiguration.calculateCellHeight();
  const bodyCellCount = Math.max(Math.round((isHorizontal ? bodySize.width : bodySize.height) / cellSize), 0);
  const portCellSlots = isHorizontal ? countPortSlots(data, "centerArray") : 2;

  if (bodyCellCount <= portCellSlots || portCellSlots === 0) return 0;
  return Math.round((bodyCellCount - portCellSlots) / 2) * cellSize;
}

function createCenterPortPanelMarginBinding() {
  return new go.Binding("margin", "", data => {
    return new go.Margin(
      calculateCenterPortPanelOffset(data, false),
      0,
      0,
      calculateCenterPortPanelOffset(data, true)
    );
  });
}

function normalizeEquipmentPortId(portId) {
  if (portId === undefined || portId === null) return "";
  const parts = portId.toString().split("_");
  return parts.length > 1 ? parts[1] : parts[0];
}

function getPortArray(nodeData, arrayName) {
  return nodeData && Array.isArray(nodeData[arrayName]) ? nodeData[arrayName] : [];
}

function getBoundPortArray(itemPanel, arrayName) {
  if (itemPanel && itemPanel.panel && Array.isArray(itemPanel.panel.itemArray)) {
    return itemPanel.panel.itemArray;
  }
  const nodeData = itemPanel && itemPanel.part && itemPanel.part.data;
  return getPortArray(nodeData, arrayName);
}

function getItemIndex(portData, itemPanel, arrayName) {
  const ports = getBoundPortArray(itemPanel, arrayName);
  const arrayIndex = ports.indexOf(portData);
  if (arrayIndex >= 0) return arrayIndex;
  return Math.max(Number(itemPanel && itemPanel.itemIndex) || 0, 0);
}

function getPortCellIndex(itemIndex, portCount, cellCount) {
  const count = Math.max(Number(portCount) || 0, 1);
  const gridCells = Math.max(Math.round(Number(cellCount) || 0), 1);
  const gap = count > 1 && (1 + ((count - 1) * 2)) <= gridCells ? 2 : 1;
  const occupiedCells = 1 + ((count - 1) * gap);
  const start = Math.max(Math.floor((gridCells - occupiedCells) / 2), 0);
  return Math.max(0, Math.min(gridCells - 1, start + (itemIndex * gap)));
}

function getPortCellCenterOffset(itemIndex, portCount, cellCount, cellSize) {
  return (getPortCellIndex(itemIndex, portCount, cellCount) + 0.5) * cellSize;
}

function getCenterCellOffset(cellCount, cellSize) {
  const gridCells = Math.max(Math.round(Number(cellCount) || 0), 1);
  return (Math.floor((gridCells - 1) / 2) + 0.5) * cellSize;
}

function createSpotPortAlignmentBinding(side, arrayName) {
  return new go.Binding("alignment", "", function(portData, itemPanel) {
    const nodeData = itemPanel && itemPanel.part && itemPanel.part.data;
    const gridSize = gridEquipmentNode.getGridSize(nodeData, equipmentGridDefaults);
    const ports = getBoundPortArray(itemPanel, arrayName);
    const itemIndex = getItemIndex(portData, itemPanel, arrayName);
    const cellSize = gridEquipmentNode.getResizeCellSize();

    if (side === "center") {
      const xOffset = getPortCellCenterOffset(itemIndex, ports.length, gridSize.cols, cellSize.width);
      return new go.Spot(0, 0, xOffset, getCenterCellOffset(gridSize.rows, cellSize.height));
    }

    if (side === "left" || side === "right") {
      const yOffset = getPortCellCenterOffset(itemIndex, ports.length, gridSize.rows, cellSize.height);
      return new go.Spot(side === "left" ? 0 : 1, 0, 0, yOffset);
    }

    const xOffset = getPortCellCenterOffset(itemIndex, ports.length, gridSize.cols, cellSize.width);
    return new go.Spot(0, side === "top" ? 0 : 1, xOffset, 0);
  });
}

function getSpotPortAlignmentFocus(side) {
  if (side === "left") return go.Spot.Right;
  if (side === "right") return go.Spot.Left;
  if (side === "top") return go.Spot.Bottom;
  if (side === "bottom") return go.Spot.Top;
  return go.Spot.Center;
}

function getSpotPortLinkSpot(side) {
  if (side === "left") return go.Spot.Left;
  if (side === "right") return go.Spot.Right;
  if (side === "top") return go.Spot.Top;
  if (side === "bottom") return go.Spot.Bottom;
  return go.Spot.AllSides;
}

function createSpotPortItemTemplate(side, arrayName, isCenterPort) {
  const linkSpot = getSpotPortLinkSpot(side);
  const linkableBindings = isCenterPort ? [
    new go.Binding("visible", "", function() {
      return edit.vm.editing() ? true : false;
    }),
    new go.Binding("fromLinkable", "", function() {
      return true;
    }),
    new go.Binding("toLinkable", "", function() {
      return true;
    }),
    new go.Binding("fromMaxLinks", "", function() {
      return 1;
    }),
    new go.Binding("toMaxLinks", "", function() {
      return 1;
    })
  ] : [
    new go.Binding("fromLinkable", "type", function(type) {
      return type === "output";
    }),
    new go.Binding("toLinkable", "type", function(type) {
      return type === "input";
    })
  ];

  return $(go.Panel, "Spot",
    {
      alignmentFocus: getSpotPortAlignmentFocus(side)
    },
    createSpotPortAlignmentBinding(side, arrayName),
    $(go.Shape, "Rectangle",
      {
        _side: side,
        cursor: "pointer",
        desiredSize: gridEquipmentNode.getResizeCellSize(),
        fromSpot: linkSpot,
        toSpot: linkSpot,
        stroke: null,
        strokeWidth: 0
      },
      ...linkableBindings,
      new go.Binding("portId", "portId", normalizeEquipmentPortId),
      new go.Binding("category", "category"),
      new go.Binding("fill", "portColor", function(portColor) {
        if (!edit.vm.editing()) return portCoverBrush;
        return portColor;
      })
    )
  );
}

function createSpotPortPanel(arrayName, side, isCenterPort) {
  return $(go.Panel, "Spot",
    {
      alignment: go.Spot.Center,
      alignmentFocus: go.Spot.Center,
      isActionable: false,
      itemTemplate: createSpotPortItemTemplate(side, arrayName, isCenterPort)
    },
    new go.Binding("itemArray", arrayName),
    $(go.Shape, "Rectangle",
      {
        isPanelMain: true,
        fill: null,
        stroke: null
      },
      gridEquipmentNode.createBodySizeBinding(equipmentGridDefaults)
    )
  );
}

function createExpandedBodySizeBinding(expansion) {
  return new go.Binding("desiredSize", "", function(data) {
    const bodySize = gridEquipmentNode.getBodySize(data, equipmentGridDefaults);
    return new go.Size(bodySize.width + expansion, bodySize.height + expansion);
  });
}

function shouldUseGridShell(node) {
  return !!(node && (
    node.gridShell === true ||
    node.gridShellPrototype === true ||
    node.gridEquipmentPrototype === true ||
    node.svgGridShellPrototype === true
  ));
}
  
function createNodeTemplate(templateMap, map, useGeoPositioning, submitCallback, nodeEquipmentArray, diagram) {
  var svgTemplatesToCreated = [];

  nodeEquipmentArray.forEach(node => {
    let classData = findClassData(node.class);
    if (node.class == 'Manifold') {
      node.category = 'Manifold';
    } else if (node.animationType == 'damper') {
      node.category = 'Damper';
    } else if (node.animationType == 'staticSvg') {
      node.category = node.key;
      if (shouldUseGridShell(node)) node.gridShell = true;
      let template = createStaticSvgTemplate(map, useGeoPositioning, submitCallback, diagram, node);
      templateMap.add(node.key, template);
    } else if (node.animationType == 'rotate') {
      node.category = node.key;
      let template = createFanNodeTemplate(map, useGeoPositioning, submitCallback, diagram, node);
      templateMap.add(node.key, template);
    } else if (node.svgConverted) {
      node.category = node.key;
      if (shouldUseGridShell(node)) node.gridShell = true;
      let template = createSvgTemplates(node.svgConverted, useGeoPositioning, map, submitCallback, node);
      templateMap.add(node.key, template);
    } else if (classData() && classData().data && classData().data.svgConverted) {
      node.category = node.class;
      node.svgConverted = classData().data.svgConverted;
      if (shouldUseGridShell(node)) node.gridShell = true;
      if (svgTemplatesToCreated.filter(className => className == node.class).length == 0) {
        let template = createSvgTemplates(node.svgConverted, useGeoPositioning, map, submitCallback, node);
        templateMap.add(node.class, template);
        svgTemplatesToCreated.push(node.class);
      }
    } else if (shouldUseGridShell(node)) {
      node.category = 'GridEquipment';
    } 

    return node;
  })

  templateMap.add('Dataset', createDatasetTemplate(map, useGeoPositioning, submitCallback, diagram));
  templateMap.add('Damper', createDamperNodeTemplate(map, useGeoPositioning, submitCallback, diagram));
  templateMap.add('Port', createPortTemplate(map, useGeoPositioning, submitCallback));
  templateMap.add('Manifold', createManifoldTemplate(map, useGeoPositioning, submitCallback));
  templateMap.add('Equipment', createEquipmentNodeTemplate(map, useGeoPositioning, submitCallback));
  templateMap.add('GridEquipment', createGridEquipmentNodeTemplate(map, useGeoPositioning, submitCallback));
  templateMap.add('Room', createRoomNodeTemplate(map, useGeoPositioning, submitCallback));
  templateMap.add('Group', createGroupNodeTemplate(map, useGeoPositioning, submitCallback));

  let svgTemplatesToCreate = nodeEquipmentArray.filter(equipment => equipment.svgConverted);
}

function createEquipmentNodeTemplate(map, useGeoPositioning, submitCallback) {
  return $(go.Node, "Table",
  {
    locationSpot: go.Spot.TopLeft,
    selectionObjectName: "BODY",
    selectionAdornmentTemplate: createEquipmentSelectionAdornmentTemplate(),
    resizeObjectName: "BODY",
    resizeCellSize: new go.Size(diagramConfiguration.calculateCellWidth(), diagramConfiguration.calculateCellHeight()),
    contextMenu: edit.vm.editing() ? $("ContextMenu",
      $("ContextMenuButton",
        $(go.TextBlock, "Edit Equipment"),
        {
          click: (e, obj) => {
            editCustomEquipmentModal.editDialogue({...obj.part.data}, { submitCallback: submitCallback, delete: true })
            m.redraw();
          }
        }
      ),
      $("ContextMenuButton",
        $(go.TextBlock, "Edit Permissions"),
        {
          click: (e, obj) => {
            m.route.set(`/equipment/${obj.part.data['class']}/${obj.part.data['key'].toString().slice(1)}/permissions`)
          }
        }
      )
    ) : null,
    doubleClick: function(e, obj) {
      // Navidate to the equipment detail page in browse mode
      if (!edit.vm.editing()) {
        m.route.set(`/equipment/${obj.part.data['class']}/${obj.part.data['key'].toString().slice(1)}`)
      }
    }
  },
  new go.Binding("cursor", "", data => {
    return edit.vm.editing() ? "pointer" : "default";
  }),
  new go.Binding("deletable", "", data => {
    return edit.vm.editing();
  }),
  new go.Binding("selectable", "", data => {
    return edit.vm.editing();
  }),
  new go.Binding("movable", "", data => {
    return edit.vm.editing();
  }),
  new go.Binding("resizable", "", data => {
    return edit.vm.editing();
  }),
  useGeoPositioning ?
    new go.Binding("location", "latlong", data => {
      const pos = map.project(data, map.getZoom());
      return new go.Point(pos.x, pos.y);
    }).makeTwoWay((pt, data) => {
      if (updatingGoJs) {
        return data.latlong; // no-op
      } else {
        const ll = map.unproject(L.point(pt.x, pt.y), map.getZoom());
        return [ll.lat, ll.lng];
      }
    })
  :
    new go.Binding("location", "latlong", data => {
      return new go.Point(data[0], data[1]);
    }),
  $(go.Panel, "Auto",
  {
    row: 1, column: 1, name: "BODY",
    stretch: go.GraphObject.Fill
  },
  new go.Binding("desiredSize", "", getEquipmentBodySize),
  $(go.Shape, "Rectangle",
    {
      fill: $(go.Brush, "Linear", { 0.0: "#2a2a2a", 1.0: "white", start: go.Spot.Bottom, end: go.Spot.Top }),
      stroke: "black",
      strokeWidth: 2,
      stretch: go.GraphObject.Fill
    }),
  ),
  $(go.Panel, "Vertical",
    new go.Binding("itemArray", "leftArray"),
    {
      row: 1, column: 0,
      alignment: go.Spot.Top,
      itemTemplate:
        $(go.Panel, "Table",
          $(go.Shape, "Rectangle",
            {
              row: 0, column: 0,
              _side: "left",
              fromSpot: go.Spot.Left,
              toSpot: go.Spot.Left,
              cursor: "pointer",
              stroke: null, strokeWidth: 0,
              desiredSize: new go.Size(diagramConfiguration.calculateCellWidth(), diagramConfiguration.calculateCellHeight())
            },
            new go.Binding("portId", "portId", function(portId) {
              return portId.split('_')[1]
            }),
            new go.Binding("category", "category"),
            new go.Binding("fromLinkable", "type", function(type) {
              return type === "output";
            }),
            new go.Binding("toLinkable", "type", function(type) {
              return type === "input";
            }),
            new go.Binding("fill", "portColor", function(portColor) {
              if(!edit.vm.editing()) {
                return portCoverBrush;
              } else {
                return portColor
              }
            })),
          $(go.Shape, "Rectangle", {
            row: 1, column: 0,
            desiredSize: new go.Size(diagramConfiguration.calculateCellWidth(), diagramConfiguration.calculateCellHeight()),
            fill: "transparent",
            stroke: null
          })
        )
    },
    createSidePortPanelMarginBinding("leftArray")
  ),
  $(go.Panel, "Horizontal",
    new go.Binding("itemArray", "topArray"),
    {
      row: 0, column: 1,
      alignment: go.Spot.Left,
      itemTemplate:
        $(go.Panel, "Table",
          $(go.Shape, "Rectangle",
            {
              row: 0, column: 0,
              _side: "top",
              fromSpot: go.Spot.Top,
              toSpot: go.Spot.Top,
              cursor: "pointer",
              stroke: null, strokeWidth: 0,
              desiredSize: new go.Size(diagramConfiguration.calculateCellWidth(), diagramConfiguration.calculateCellHeight())
            },
            new go.Binding("portId", "portId", function(portId) {
              return portId.split('_')[1]
            }),
            new go.Binding("category", "category"),
            new go.Binding("fromLinkable", "type", function(type) {
              return type === "output";
            }),
            new go.Binding("toLinkable", "type", function(type) {
              return type === "input";
            }),
            new go.Binding("fill", "portColor", function(portColor) {
              if(!edit.vm.editing()) {
                return portCoverBrush;
              } else {
                return portColor
              }
            })),
          $(go.Shape, "Rectangle", {
            row: 0, column: 1,
            desiredSize: new go.Size(diagramConfiguration.calculateCellWidth(), diagramConfiguration.calculateCellHeight()),
            fill: "transparent",
            stroke: null
          })
        )
    },
    createTopBottomPortPanelMarginBinding("topArray")
  ),
  $(go.Panel, "Vertical",
    new go.Binding("itemArray", "rightArray"),
    {
      row: 1, column: 2,
      alignment: go.Spot.Top,
      itemTemplate:
        $(go.Panel, "Table",
          $(go.Shape, "Rectangle",
            {
              row: 0, column: 0,
              _side: "right",
              fromSpot: go.Spot.Right,
              toSpot: go.Spot.Right,
              cursor: "pointer",
              stroke: null, strokeWidth: 0,
              desiredSize: new go.Size(diagramConfiguration.calculateCellWidth(), diagramConfiguration.calculateCellHeight())
            },
            new go.Binding("portId", "portId", function(portId) {
              return portId.split('_')[1]
            }),
            new go.Binding("category", "category"),
            new go.Binding("fromLinkable", "type", function(type) {
              return type === "output";
            }),
            new go.Binding("toLinkable", "type", function(type) {
              return type === "input";
            }),
            new go.Binding("fill", "portColor", function(portColor) {
              if(!edit.vm.editing()) {
                return portCoverBrush;
              } else {
                return portColor
              }
            })),
          $(go.Shape, "Rectangle", {
            row: 1, column: 0,
            desiredSize: new go.Size(diagramConfiguration.calculateCellWidth(), diagramConfiguration.calculateCellHeight()),
            fill: "transparent",
            stroke: null
          })
        )
    },
    createSidePortPanelMarginBinding("rightArray")
  ),
  $(go.Panel, "Horizontal",
    new go.Binding("itemArray", "bottomArray"),
    {
      row: 2, column: 1,
      alignment: go.Spot.Left,
      itemTemplate:
        $(go.Panel, "Table",
          $(go.Shape, "Rectangle",
            {
              row: 0, column: 0,
              _side: "bottom",
              fromSpot: go.Spot.Bottom,
              toSpot: go.Spot.Bottom,
              cursor: "pointer",
              stroke: null, strokeWidth: 0,
              desiredSize: new go.Size(diagramConfiguration.calculateCellWidth(), diagramConfiguration.calculateCellHeight())
            },
            new go.Binding("portId", "portId", function(portId) {
              return portId.split('_')[1]
            }),
            new go.Binding("category", "category"),
            new go.Binding("fromLinkable", "type", function(type) {
              return type === "output";
            }),
            new go.Binding("toLinkable", "type", function(type) {
              return type === "input";
            }),
            new go.Binding("fill", "portColor", function(portColor) {
              if(!edit.vm.editing()) {
                return portCoverBrush;
              } else {
                return portColor
              }
            })),
          $(go.Shape, "Rectangle", {
            row: 0, column: 1,
            desiredSize: new go.Size(diagramConfiguration.calculateCellWidth(), diagramConfiguration.calculateCellHeight()),
            fill: "transparent",
            stroke: null
          })
        )
    },
    createTopBottomPortPanelMarginBinding("bottomArray")
  ),
  $(go.Panel, "Horizontal",
    new go.Binding("itemArray", "centerArray"),
    {
      row: 1, column: 1,
      alignment: go.Spot.TopLeft,
      itemTemplate:
        $(go.Panel, "Table",
          $(go.Shape, "Rectangle",
            {
              row: 0, column: 0,
              alignment: go.Spot.Center,
              fromSpot: go.Spot.AllSides,
              toSpot: go.Spot.AllSides,
              fromLinkable: true,
              toLinkable: true,
              fromMaxLinks: 1,
              toMaxLinks: 1,
              visible: edit.vm.editing() ? true : false,
              stroke: null, strokeWidth: 0,
              desiredSize: new go.Size(diagramConfiguration.calculateCellWidth(), diagramConfiguration.calculateCellHeight())
            },
            new go.Binding("portId", "portId", function(portId) {
              return portId.split('_')[1]
            }),
            new go.Binding("category", "category"),
            new go.Binding("fill", "portColor", function(portColor) {
              if(!edit.vm.editing()) {
                return portCoverBrush;
              } else {
                return portColor
              }
            })),
          $(go.Shape, "Rectangle", {
            row: 0, column: 1,
            desiredSize: new go.Size(diagramConfiguration.calculateCellWidth(), diagramConfiguration.calculateCellHeight()),
            fill: "transparent",
            stroke: null
          }),
          $(go.Shape, "Rectangle", {
            row: 1, column: 0,
            desiredSize: new go.Size(diagramConfiguration.calculateCellWidth(), diagramConfiguration.calculateCellHeight()),
            fill: "transparent",
            stroke: null
          })
        )
    },
    createCenterPortPanelMarginBinding()
  ),
  createExternalConnectionAnnotations()
);
}

function createGridEquipmentNodeTemplate(map, useGeoPositioning, submitCallback) {
  return $(go.Node, "Spot",
    {
      locationSpot: go.Spot.TopLeft,
      locationObjectName: "BODY",
      selectionObjectName: "BODY",
      selectionAdornmentTemplate: gridEquipmentNode.createSelectionAdornmentTemplate(),
      resizeObjectName: "BODY",
      resizeCellSize: gridEquipmentNode.getResizeCellSize(),
      contextMenu: edit.vm.editing() ? $("ContextMenu",
        $("ContextMenuButton",
          $(go.TextBlock, "Edit Equipment"),
          {
            click: (e, obj) => {
              editCustomEquipmentModal.editDialogue({...obj.part.data}, { submitCallback: submitCallback, delete: true })
              m.redraw();
            }
          }
        ),
        $("ContextMenuButton",
          $(go.TextBlock, "Edit Permissions"),
          {
            click: (e, obj) => {
              m.route.set(`/equipment/${obj.part.data['class']}/${obj.part.data['key'].toString().slice(1)}/permissions`)
            }
          }
        )
      ) : null,
      doubleClick: function(e, obj) {
        // Navidate to the equipment detail page in browse mode
        if (!edit.vm.editing()) {
          m.route.set(`/equipment/${obj.part.data['class']}/${obj.part.data['key'].toString().slice(1)}`)
        }
      }
    },
    new go.Binding("cursor", "", data => {
      return edit.vm.editing() ? "pointer" : "default";
    }),
    new go.Binding("deletable", "", data => {
      return edit.vm.editing();
    }),
    new go.Binding("selectable", "", data => {
      return edit.vm.editing();
    }),
    new go.Binding("movable", "", data => {
      return edit.vm.editing();
    }),
    new go.Binding("resizable", "", data => {
      return edit.vm.editing();
    }),
    useGeoPositioning ?
      new go.Binding("location", "latlong", data => {
        const pos = map.project(data, map.getZoom());
        return new go.Point(pos.x, pos.y);
      }).makeTwoWay((pt, data) => {
        if (updatingGoJs) {
          return data.latlong; // no-op
        } else {
          const ll = map.unproject(L.point(pt.x, pt.y), map.getZoom());
          return [ll.lat, ll.lng];
        }
      })
    :
      new go.Binding("location", "latlong", data => {
        return new go.Point(data[0], data[1]);
      }),
    $(go.Panel, "Spot",
      {
        name: "BODY",
        isPanelMain: true
      },
      gridEquipmentNode.createBodySizeBinding(equipmentGridDefaults),
      $(go.Shape, "Rectangle",
        {
          isPanelMain: true,
          fill: $(go.Brush, "Linear", { 0.0: "#2a2a2a", 1.0: "white", start: go.Spot.Bottom, end: go.Spot.Top }),
          stroke: null,
          stretch: go.GraphObject.Fill
        })
    ),
    $(go.Shape, "Rectangle",
      {
        alignment: go.Spot.Center,
        alignmentFocus: go.Spot.Center,
        fill: null,
        pickable: false,
        stroke: "black",
        strokeWidth: equipmentBodyBorderStrokeWidth
      },
      createExpandedBodySizeBinding(equipmentBodyBorderStrokeWidth)
    ),
    createSpotPortPanel("leftArray", "left"),
    createSpotPortPanel("topArray", "top"),
    createSpotPortPanel("rightArray", "right"),
    createSpotPortPanel("bottomArray", "bottom"),
    createSpotPortPanel("centerArray", "center", true)
  );
}

function findClassData(className) {
  let classData = prop();
  equipmentModel.equipmentClassTree.categories.forEach((item) => {
    if (item.name == className && !classData()) {
      classData(item);
    } else {
      if(item.children) {
        findClassDataInTree(className, item, classData);
      }
    }
  })
  return classData;
}

function findClassDataInTree(className, item, classData) {
  item.children.forEach((childItem) => {
    if (childItem.name == className && !classData()) {
      classData(childItem);
    } else {
      if (childItem.children) {
        findClassDataInTree(className, childItem, classData);
      }
    }
  })
}

module.exports = createNodeTemplate;
